// QR Kiosk v2 — §8.6 composer (chat-stage footer). Auto-grow reply textarea, cam
// attach + pending-image preview, send ↕ / stop ■ flip, dim (not hide) while
// streaming. Round-6: the §8.1 mount autofocus was REMOVED (owner reversal) — the
// takeover opens keyboard-free on the welcome screen; the keyboard appears only when
// the user taps this input. `enterKeyHint="send"` is kept. §8.6: removed the "enter to
// send" / "MIDAS can make mistakes" hint row, bigger camera (44×44, glyph 24) + 2× wider
// send (68×44), better placeholder, and the recalibrated 15.5px textarea (the .composer
// class — the old inline fontSize:13.5 was a silent regression). Tokens + glyphs only.
import React, { useRef, useState } from "react";
import useTranslation from "../../../shared/hooks/useTranslation";
import { Glyph, GD } from "../shared/glyphs";
import { usePressed } from "../shared/usePressed";

const MAX_TEXTAREA_H = 96; // ~4 lines at 22px line-height

export interface MidasComposerProps {
  isLoading: boolean;
  onSend: (text: string, image?: File) => void;
  onStop: () => void;
}

export function MidasComposer({ isLoading, onSend, onStop }: MidasComposerProps) {
  const { translate } = useTranslation("public");
  const [text, setText] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const sendPress = usePressed();
  const camPress = usePressed();

  const grow = () => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, MAX_TEXTAREA_H)}px`;
  };

  const clearImage = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setImage(null);
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (preview) URL.revokeObjectURL(preview);
    setImage(file);
    setPreview(URL.createObjectURL(file));
  };

  const doSend = () => {
    if (isLoading) return;
    const t = text.trim();
    if (!t && !image) return;
    onSend(t, image ?? undefined);
    setText("");
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setImage(null);
    if (taRef.current) taRef.current.style.height = "auto";
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      doSend();
    }
  };

  return (
    <div
      style={{
        flexShrink: 0,
        padding: "10px 14px calc(14px + env(safe-area-inset-bottom))",
        borderTop: "1px solid var(--border-soft)",
        background: "var(--card)",
      }}
    >
      {preview && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, padding: "6px 8px", borderRadius: 10, background: "var(--bg)", border: "1px solid var(--border)", maxWidth: "100%" }}>
          <img src={preview} alt="" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 8, flexShrink: 0 }} />
          <span style={{ flex: 1, minWidth: 0, fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink-2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {image?.name}
          </span>
          <button type="button" aria-label={translate("kioskV2AriaRemoveImage")} onClick={clearImage} style={{ width: 24, height: 24, borderRadius: 999, border: "none", background: "transparent", color: "var(--muted)", display: "grid", placeItems: "center", cursor: "pointer", flexShrink: 0 }}>
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
              <path d="M6 6 L18 18" />
              <path d="M18 6 L6 18" />
            </svg>
          </button>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, minHeight: 56, padding: "6px 6px 6px 14px", borderRadius: 14, background: "var(--bg)", border: "1px solid var(--border)", opacity: isLoading ? 0.6 : 1 }}>
        <textarea
          ref={taRef}
          className="composer"
          rows={1}
          value={text}
          readOnly={isLoading}
          // enterKeyHint lives in AllHTMLAttributes (not TextareaHTMLAttributes) in
          // this @types/react — the DOM supports it, so apply via a typed spread so
          // the keyboard shows a Send action key without a raw `any`.
          {...({ enterKeyHint: "send" } as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
          placeholder={translate("kioskV2MidasInputPlaceholder")}
          onChange={(e) => {
            setText(e.target.value);
            grow();
          }}
          onKeyDown={onKeyDown}
          style={{ flex: 1, minWidth: 0, maxHeight: MAX_TEXTAREA_H, alignSelf: "center" }}
        />

        <label
          htmlFor="kv2-midas-cam"
          {...camPress.pressHandlers}
          aria-label={translate("kioskV2MidasAttachPhoto")}
          style={{ width: 44, height: 44, borderRadius: 11, display: "grid", placeItems: "center", color: "var(--ink-2)", cursor: "pointer", flexShrink: 0, ...camPress.pressStyle }}
        >
          <Glyph d={GD.cam} size={24} />
        </label>
        <input id="kv2-midas-cam" type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={onPick} />

        {isLoading ? (
          <button
            type="button"
            aria-label={translate("kioskV2MidasStop")}
            onClick={onStop}
            style={{ width: 68, height: 44, borderRadius: 11, background: "var(--card)", border: "1px solid var(--border)", display: "grid", placeItems: "center", cursor: "pointer", flexShrink: 0 }}
          >
            <span style={{ width: 13, height: 13, borderRadius: 3, background: "var(--ink-2)" }} />
          </button>
        ) : (
          <button
            type="button"
            aria-label={translate("kioskV2MidasSend")}
            {...sendPress.pressHandlers}
            onClick={doSend}
            style={{ width: 68, height: 44, borderRadius: 11, background: "var(--midas)", color: "var(--tier-accent-fg)", border: "none", display: "grid", placeItems: "center", cursor: "pointer", flexShrink: 0, ...sendPress.pressStyle }}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 19 V5" />
              <path d="M6 11 L12 5 L18 11" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

export default MidasComposer;
